import { windowLabel } from "@/lib/content";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg, money } from "@/lib/api";

let sdkPromise = null;
const loadPayPal = (clientId, currency) => {
  if (window.paypal) return Promise.resolve(window.paypal);
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=${currency}&intent=capture`;
      s.onload = () => resolve(window.paypal);
      s.onerror = () => { sdkPromise = null; reject(); };
      document.body.appendChild(s);
    });
  }
  return sdkPromise;
};

const PayPalButtons = ({ config, token, paypalOrderId, onDone, onError }) => {
  const ref = useRef(null);
  const cb = useRef({ onDone, onError });
  cb.current = { onDone, onError };
  const clientId = config.paypal.client_id;
  const currency = config.currency;
  useEffect(() => {
    let instance = null;
    let cancelled = false;
    loadPayPal(clientId, currency).then((paypal) => {
      if (cancelled || !ref.current) return;
      instance = paypal.Buttons({
        fundingSource: paypal.FUNDING.PAYPAL,
        createOrder: () => paypalOrderId,
        onApprove: async (data) => {
          try { cb.current.onDone((await api.post(`/orders/${token}/paypal/capture`, { paypal_order_id: data.orderID })).data); } catch (e) { cb.current.onError(errMsg(e)); }
        },
        onCancel: async () => { await api.post(`/orders/${token}/paypal/cancel`, { paypal_order_id: paypalOrderId }).catch(() => {}); cb.current.onError("Payment was cancelled. You can try again whenever you're ready."); },
        onError: () => cb.current.onError("PayPal couldn't complete the payment. Please try again."),
      });
      if (instance.isEligible && !instance.isEligible()) return cb.current.onError("PayPal isn't available in this browser.");
      instance.render(ref.current).catch(() => { if (!cancelled) cb.current.onError("PayPal couldn't load. Please refresh and try again."); });
    }).catch(() => cb.current.onError("Couldn't load PayPal. Check your connection and try again."));
    return () => { cancelled = true; if (instance) instance.close().catch(() => {}); };
  }, [clientId, currency, token, paypalOrderId]);
  return <div ref={ref} className="min-h-[50px]" data-testid="create-wizard-paypal-button" />;
};

const DevPayment = ({ token, paymentId, onDone, onError }) => {
  const pay = async (outcome) => {
    try { onDone((await api.post(`/orders/${token}/dev-payment`, { payment_id: paymentId, outcome })).data); } catch (e) { onError(errMsg(e)); }
  };
  return (
    <div className="rounded-2xl border-2 border-dashed border-[var(--gold)] p-6" data-testid="dev-payment-panel">
      <p className="font-semibold">Development payment simulator</p>
      <p className="mt-1 text-sm text-[var(--plum-2)]">PayPal isn't connected yet. No money moves. Simulated payments are recorded as such and are refused in production mode.</p>
      <div className="mt-4 flex flex-wrap gap-3">
        <button className="btn-plum !py-2.5" onClick={() => pay("completed")} data-testid="dev-pay-complete">Simulate successful payment</button>
        <button className="btn-ghost !py-2.5" onClick={() => pay("pending")} data-testid="dev-pay-pending">Simulate pending</button>
        <button className="btn-ghost !py-2.5" onClick={() => pay("cancelled")} data-testid="dev-pay-cancel">Simulate cancel</button>
      </div>
    </div>
  );
};

const Row = ({ k, v, testid }) => (
  <div className="flex justify-between gap-6 border-b border-[var(--parchment)] py-3 text-sm last:border-0">
    <dt className="text-[var(--plum-muted)]">{k}</dt><dd className="text-right font-medium" data-testid={testid}>{v}</dd>
  </div>
);

export const StepReview = ({ order, config, token, onPaid }) => {
  const [email, setEmail] = useState(order.buyer_email || "");
  const [terms, setTerms] = useState(false);
  const [consent, setConsent] = useState(false);
  const [checkout, setCheckout] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setErr(""); setBusy(true);
    try {
      const r = await api.post(`/orders/${token}/checkout`, { buyer_email: email, accept_terms: terms, immediate_consent: consent, terms_version: config.terms_version });
      setCheckout(r.data);
    } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };
  const done = (o) => {
    if (o.payment_status === "completed" || o.payment_status === "pending") onPaid(o);
    else setErr(o.payment_status === "cancelled" ? "Payment was cancelled. You can try again." : "Payment didn't complete. Please try again.");
    if (o.payment_status !== "completed" && o.payment_status !== "pending") setCheckout(null);
  };
  const lang = order.song_language;

  return (
    <div className="grid gap-8 lg:grid-cols-5">
      <dl className="card-white p-6 lg:col-span-3" data-testid="order-summary">
        <Row k="Product" v={config.product_name} />
        <Row k="Recipient" v={order.recipient.name} testid="summary-recipient" />
        <Row k="Occasion" v={order.custom_occasion_label ? `${order.occasion_label} — ${order.custom_occasion_label}` : order.occasion_label} testid="summary-occasion" />
        <Row k="Song language" v={<>{lang.english_name}{lang.native_name !== lang.english_name && <> (<bdi lang={lang.code}>{lang.native_name}</bdi>)</>}{order.song_language_variant_label && ` · ${order.song_language_variant_label}`}</>} testid="summary-language" />
        <Row k="Style & tone" v={`${order.style_label} · ${order.tone}`} />
        <Row k="Approved lyrics" v={<>“<bdi lang={lang.code}>{order.lyrics.title}</bdi>” (v{order.lyrics.version})</>} testid="summary-lyrics" />
        <Row k="Delivery" v={config.fulfillment_window || "After generation and review — no guaranteed date"} />
        <Row k="Revision" v={`${config.revision_count} within ${windowLabel(config.revision_window_days)} of delivery`} />
        {config.compare_at_price > config.price && <Row k="Original price" v={<s>{money(config.compare_at_price, config.currency)}</s>} testid="summary-original" />}
        <Row k={config.compare_at_price > config.price ? `Total (${Math.round((1 - config.price / config.compare_at_price) * 100)}% OFF)` : "Total"} v={money(config.price, config.currency)} testid="summary-total" />
        <p className="mt-4 text-xs text-[var(--plum-muted)]">{config.ai_disclosure} Revisions: {config.revision_covers}</p>
      </dl>
      <div className="space-y-5 lg:col-span-2">
        <label className="block"><span className="label">Your email *</span>
          <input type="email" required className="field" value={email} onChange={(e) => setEmail(e.target.value)} disabled={!!checkout} data-testid="checkout-email-input" />
          <span className="hint block">We'll use this for order updates only — no marketing.</span></label>
        <label className="flex items-start gap-3 text-sm">
          <Checkbox checked={terms} onCheckedChange={(v) => setTerms(!!v)} disabled={!!checkout} data-testid="checkout-terms-checkbox" className="mt-0.5" />
          <span>I agree to the <Link to="/terms" target="_blank" className="underline">Terms</Link> and <Link to="/refunds" target="_blank" className="underline">Refund & Revision Policy</Link> (version {config.terms_version}), including personal-use terms for the song.</span>
        </label>
        <label className="flex items-start gap-3 text-sm">
          <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} disabled={!!checkout} data-testid="checkout-consent-checkbox" className="mt-0.5" />
          <span>I ask you to start creating my song right after payment, and I understand this is a personalized digital product.</span>
        </label>
        {err && <p className="text-sm text-[var(--rose)]" role="alert" data-testid="checkout-error">{err}</p>}
        {!checkout ? (
          <button className="btn-gold w-full" disabled={!email || !terms || !consent || busy} onClick={start} data-testid="checkout-continue-button">
            {busy ? "Preparing…" : `Continue to payment · ${money(config.price, config.currency)}`}
          </button>
        ) : checkout.mode === "paypal" ? (
          <PayPalButtons config={config} token={token} paypalOrderId={checkout.paypal_order_id} onDone={done} onError={setErr} />
        ) : (
          <DevPayment token={token} paymentId={checkout.payment_id} onDone={done} onError={setErr} />
        )}
      </div>
    </div>
  );
};
